// @ts-nocheck
import React, { useState } from 'react';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Plus,
  Search,
  Star,
  Users,
  Clock,
  Mail,
  ArrowLeft,
  Calendar,
  Target,
  MessageSquare,
  TrendingUp,
  Eye,
  Copy
} from "lucide-react";

const templateCategories = [
  { value: "all", label: "All Templates", count: 12 },
  { value: "weekly", label: "Weekly Check-ins", count: 4 },
  { value: "performance", label: "Performance", count: 3 },
  { value: "career", label: "Career Development", count: 2 },
  { value: "project", label: "Project-based", count: 2 },
  { value: "email", label: "Email 1-1s", count: 1 }
];

const oneOnOneTemplates = [
  {
    id: "weekly-checkin",
    name: "Weekly Check-in",
    description: "Regular weekly check-in to discuss ongoing work, blockers, and priorities",
    category: "weekly",
    agendaItems: [
      "What did you accomplish this week?",
      "What challenges are you facing?",
      "What support do you need from me?",
      "How are you feeling about your current workload?",
      "Any blockers I can help remove?"
    ],
    createdBy: {
      name: "Sarah Johnson",
      avatar: null
    },
    usageCount: 47,
    lastUsed: "2 days ago",
    isDefault: true,
    estimatedTime: "30 mins",
    type: "in-person"
  },
  {
    id: "performance-review",
    name: "Performance Review",
    description: "Quarterly performance review and goal setting session",
    category: "performance",
    agendaItems: [
      "Review goals from last quarter",
      "Discuss key accomplishments",
      "Areas for improvement",
      "Set goals for next quarter",
      "Career development discussion",
      "Feedback on management style"
    ],
    createdBy: {
      name: "Mike Chen",
      avatar: null
    },
    usageCount: 23,
    lastUsed: "1 week ago",
    isDefault: true,
    estimatedTime: "60 mins",
    type: "in-person"
  },
  {
    id: "career-development",
    name: "Career Development",
    description: "Focus on long-term career growth and development opportunities",
    category: "career",
    agendaItems: [
      "Where do you see yourself in 6-12 months?",
      "What skills would you like to develop?",
      "What projects interest you most?",
      "How can I support your career goals?",
      "Training or conference requests"
    ],
    createdBy: {
      name: "Emily Davis",
      avatar: null
    },
    usageCount: 31,
    lastUsed: "3 days ago",
    isDefault: false,
    estimatedTime: "45 mins",
    type: "in-person"
  },
  {
    id: "project-kickoff",
    name: "Project Kickoff",
    description: "Initial discussion for a new project assignment",
    category: "project",
    agendaItems: [
      "Project overview and objectives",
      "Your role and responsibilities",
      "Timeline and milestones",
      "Resources and support needed",
      "Questions or concerns about the project"
    ],
    createdBy: {
      name: "Alex Rodriguez",
      avatar: null
    },
    usageCount: 18,
    lastUsed: "5 days ago",
    isDefault: false,
    estimatedTime: "45 mins",
    type: "in-person"
  },
  {
    id: "email-weekly",
    name: "Email Weekly Check-in",
    description: "Async weekly check-in template for email-based one-on-ones",
    category: "email",
    agendaItems: [
      "This week's accomplishments and wins",
      "Current challenges or roadblocks",
      "Priority tasks for next week",
      "Any support or resources needed",
      "Team collaboration updates",
      "Personal well-being check"
    ],
    createdBy: {
      name: "Jessica Kim",
      avatar: null
    },
    usageCount: 89,
    lastUsed: "Today",
    isDefault: false,
    estimatedTime: "15 mins to complete",
    type: "email"
  },
  {
    id: "feedback-focused",
    name: "Feedback-Focused Session",
    description: "Dedicated session for giving and receiving feedback",
    category: "performance",
    agendaItems: [
      "Recent work I'd like to give you feedback on",
      "Feedback you have for me",
      "Team dynamics and collaboration",
      "Process improvements we could make",
      "What's working well that we should continue?"
    ],
    createdBy: {
      name: "David Park",
      avatar: null
    },
    usageCount: 12,
    lastUsed: "1 week ago",
    isDefault: false,
    estimatedTime: "30 mins",
    type: "in-person"
  }
];

const categoryIcons = {
  weekly: Clock,
  performance: TrendingUp,
  career: Target,
  project: Calendar,
  email: Mail,
  feedback: MessageSquare
};

// Utility functions
const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

const getAvatarColor = (name: string) => {
  const colors = [
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
    'bg-purple-100 text-purple-700',
    'bg-orange-100 text-orange-700',
    'bg-pink-100 text-pink-700',
  ];
  const index = name.length % colors.length;
  return colors[index];
};

export function OneOnOneTemplatesPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredTemplates = oneOnOneTemplates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryInfo = (category: string) => {
    return templateCategories.find(cat => cat.value === category);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center border-b border-border pb-6 mb-8">
          <div className="flex items-center gap-4">
            <Link to="/one-on-ones">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to One-on-Ones
              </Button>
            </Link>
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl flex items-center justify-center">
              <FileText className="h-8 w-8 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">1-1 Templates</h1>
              <p className="text-muted-foreground text-lg">Pre-built templates to structure your one-on-one conversations</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/one-on-ones/templates/create">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl flex items-center justify-center">
                  <FileText className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{oneOnOneTemplates.length}</div>
                  <div className="text-sm text-muted-foreground">Total Templates</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl flex items-center justify-center">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {oneOnOneTemplates.reduce((sum, t) => sum + t.usageCount, 0)}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Uses</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl flex items-center justify-center">
                  <Star className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {oneOnOneTemplates.filter(t => t.isDefault).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Default Templates</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl flex items-center justify-center">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {oneOnOneTemplates.filter(t => t.type === 'email').length}
                  </div>
                  <div className="text-sm text-muted-foreground">Email Templates</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex flex-1 gap-4 w-full md:w-auto">
                <div className="relative flex-1 md:max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    {templateCategories.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        <div className="flex items-center justify-between w-full">
                          <span>{category.label}</span>
                          <Badge variant="outline" className="ml-2">
                            {category.count}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="text-sm text-muted-foreground">
                {filteredTemplates.length} of {oneOnOneTemplates.length} templates
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {filteredTemplates.map((template) => {
            const CategoryIcon = categoryIcons[template.category as keyof typeof categoryIcons] || FileText;

            return (
              <Card key={template.id} className="hover:shadow-md transition-shadow group">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl flex items-center justify-center">
                        <CategoryIcon className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg text-foreground mb-1">{template.name}</CardTitle>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge
                            variant="outline"
                            className="text-xs bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-indigo-200"
                          >
                            {getCategoryInfo(template.category)?.label}
                          </Badge>
                          {template.isDefault && (
                            <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                          {template.type === 'email' && (
                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                              <Mail className="h-3 w-3 mr-1" />
                              Email
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <CardDescription className="text-muted-foreground mt-2">
                    {template.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Agenda Preview */}
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">
                      Agenda Items ({template.agendaItems.length})
                    </h4>
                    <div className="space-y-1">
                      {template.agendaItems.slice(0, 3).map((item, index) => (
                        <div key={index} className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1">
                          {item}
                        </div>
                      ))}
                      {template.agendaItems.length > 3 && (
                        <div className="text-xs text-muted-foreground px-2">
                          +{template.agendaItems.length - 3} more items...
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Template Stats */}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {template.usageCount} uses
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {template.estimatedTime}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={template.createdBy.avatar} alt={template.createdBy.name} />
                        <AvatarFallback className={`text-xs ${getAvatarColor(template.createdBy.name)}`}>
                          {getInitials(template.createdBy.name)}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <Link to={`/one-on-ones/create?template=${template.id}`} className="flex-1">
                      <Button size="sm" className="w-full">
                        <Calendar className="h-3 w-3 mr-1" />
                        Use Template
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" className="px-3">
                      <Eye className="h-3 w-3" />
                    </Button>
                    <Button variant="outline" size="sm" className="px-3">
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-muted/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FileText className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">No templates found</h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery || selectedCategory !== "all"
                ? "Try adjusting your search or filter criteria"
                : "Get started by creating your first one-on-one template"}
            </p>
            <Link to="/one-on-ones/templates/create">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Template
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
